import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Overview from './Overview';
import Video from './Video';
import Photos from './Photos';
import PointCloud from './PointCloud';
import Model from './Model';
import Orthophotos from './Orthophotos';
import Plan from './Plan';
import MapView from './MapView';
import Downloads from './Downloads';
import InspectableMap from './InspectableMap';
import GaussianSplatting from './GaussianSplatting';
import SidebarContent from './SidebarContent';
import PasswordModal from './PasswordModal';
import './ProjectPage.css';
import { FaMapMarkedAlt, FaSignOutAlt } from 'react-icons/fa'; // Importing icons

const ProjectPage = () => {
  const { code } = useParams();
  const [projectData, setProjectData] = useState(null);
  const [error, setError] = useState(null);
  const [currentSection, setCurrentSection] = useState('overview');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedOrthophoto, setSelectedOrthophoto] = useState(null);
  const [selectedPointCloud, setSelectedPointCloud] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedDownload, setSelectedDownload] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedInspectableMap, setSelectedInspectableMap] = useState(null);
  const [selected3DGS, setSelected3DGS] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Function to preload images
  const preloadImages = (imageUrls) => {
    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the specific project JSON file based on the project code
        const response = await fetch(`/data/projects/${code}.json`);
        if (!response.ok) {
          throw new Error('Project not found');
        }
        const project = await response.json();
        setProjectData(project);
        setIsPasswordProtected(!!project.password);
        setIsAuthenticated(!project.password);

        // Set the first items by default
        setSelectedOrthophoto(project?.orthophotos ? project.orthophotos[0]?.link : null);
        setSelectedVideo(project?.videos ? project.videos[0]?.link : null);
        setSelectedPointCloud(project?.pointClouds ? project.pointClouds[0]?.link : null);
        setSelectedModel(project?.models ? project.models[0]?.link : null);
        setSelectedPlan(project?.plans ? project.plans[0]?.file : null);
        setSelectedDownload(project?.downloads ? project.downloads[0]?.link : null);
        setSelectedInspectableMap(project?.inspectableMap?.link || null);
        setSelected3DGS(project?.['3dgs'] ? project['3dgs'][0]?.link : null);

        // Preload images (photos, orthophotos thumbnails, and project thumbnail)
        if (project) {
          const imageUrls = [];

          // Preload photos
          if (project.images && project.images.length > 0) {
            imageUrls.push(...project.images);
          }

          // Preload orthophoto thumbnails
          if (project.orthophotos && project.orthophotos.length > 0) {
            const orthophotoThumbs = project.orthophotos.map((ortho) => ortho.orthothumb);
            imageUrls.push(...orthophotoThumbs);
          }

          // Preload project thumbnail
          if (project.thumbnail) {
            imageUrls.push(project.thumbnail);
          }

          // Preload the collected images
          preloadImages(imageUrls);
        }
      } catch (error) {
        setError(error.message);
      }
    };

    fetchData();
  }, [code]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!projectData) {
    return <div>Loading...</div>;
  }

  const handlePasswordSubmit = (password) => {
    if (password === projectData.password) {
      setIsAuthenticated(true);
    }
  };

  if (isPasswordProtected && !isAuthenticated) {
    return <PasswordModal onPasswordSubmit={handlePasswordSubmit} isOpen={true} />;
  }

  const { name, videos, images, pointClouds, models, orthophotos, plans, downloads, inspectableMap, lat, lng, address } = projectData;
  const gaussianSplatting = projectData['3dgs'];

  return (
    <div className="project-page">
      <header className="project-header">
        <div className="burger-menu" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</div>
        <nav className="horizontal-menu">
          {name && (
            <a 
              className={currentSection === 'overview' ? 'active' : ''}
              onClick={() => setCurrentSection('overview')}
            >
              Résumé
            </a>
          )}
          {videos && (
            <a 
              className={currentSection === 'video' ? 'active' : ''}
              onClick={() => setCurrentSection('video')}
            >
              Vidéos
            </a>
          )}
          {images && (
            <a 
              className={currentSection === 'photos' ? 'active' : ''}
              onClick={() => setCurrentSection('photos')}
            >
              Photos
            </a>
          )}
          {pointClouds && (
            <a 
              className={currentSection === 'pointCloud' ? 'active' : ''}
              onClick={() => setCurrentSection('pointCloud')}
            >
              Nuage de points
            </a>
          )}
          {models && (
            <a 
              className={currentSection === 'model' ? 'active' : ''}
              onClick={() => setCurrentSection('model')}
            >
              Modèle 3D
            </a>
          )}
          {orthophotos && (
            <a 
              className={currentSection === 'orthophotos' ? 'active' : ''}
              onClick={() => setCurrentSection('orthophotos')}
            >
              Orthophotographies
            </a>
          )}
          {gaussianSplatting && (
            <a 
              className={currentSection === '3dgs' ? 'active' : ''}
              onClick={() => setCurrentSection('3dgs')}
            >
              3DGS
            </a>
          )}
          {plans && (
            <a 
              className={currentSection === 'plans' ? 'active' : ''}
              onClick={() => setCurrentSection('plans')}
            >
              Plans
            </a>
          )}
          {downloads && (
            <a 
              className={currentSection === 'downloads' ? 'active' : ''}
              onClick={() => setCurrentSection('downloads')}
            >
              Téléchargements
            </a>
          )}
          {inspectableMap && (
            <a 
              className={currentSection === 'inspectableMap' ? 'active' : ''}
              onClick={() => setCurrentSection('inspectableMap')}
            >
              Carte Inspectable
            </a>
          )}
          {lat && lng && address && (
            <a 
              className={currentSection === 'carte' ? 'active' : ''}
              onClick={() => setCurrentSection('carte')}
            >
              Carte
            </a>
          )}
          <div className="header-buttons">
            <Link to="/" className="header-button exit-button" title="Retour à l'écran de choix de projet">
              <FaSignOutAlt />
            </Link>
            <a href="https://scan3dpatrimoine.com/carte-interactive/" className="header-button map-button" rel="noopener noreferrer" title="Retour à la Carte Interactive">
              <FaMapMarkedAlt />
            </a>
          </div>
        </nav>
      </header>

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <SidebarContent
          section={currentSection}
          project={projectData}
          setSelectedPhoto={setSelectedPhoto}
          setSelectedVideo={setSelectedVideo}
          setSelectedOrthophoto={setSelectedOrthophoto}
          setSelectedPointCloud={setSelectedPointCloud}
          setSelectedModel={setSelectedModel}
          setSelectedDownload={setSelectedDownload}
          setSelectedPlan={setSelectedPlan}
          setSelected3DGS={setSelected3DGS}
        />
      </aside>

      <main className={`main-viewspace ${sidebarOpen ? 'open-sidebar' : ''}`}>
        {currentSection === 'overview' && <Overview project={projectData} />}
        {currentSection === 'video' && <Video selectedVideo={selectedVideo} />}
        {currentSection === 'photos' && <Photos photos={images} selectedPhoto={selectedPhoto} />}
        {currentSection === 'pointCloud' && <PointCloud link={selectedPointCloud} />}
        {currentSection === 'model' && <Model link={selectedModel} niraAccountType={projectData?.models?.find(m => m.link === selectedModel)?.niraAccountType} />}
        {currentSection === 'orthophotos' && <Orthophotos selectedOrthophoto={selectedOrthophoto} />}
        {currentSection === '3dgs' && <GaussianSplatting selected3DGS={selected3DGS} />}
        {currentSection === 'plans' && <Plan link={selectedPlan} />}
        {currentSection === 'downloads' && <Downloads link={selectedDownload} />}
        {currentSection === 'carte' && lat && lng && address && <MapView lat={lat} lng={lng} address={address} />}
        {currentSection === 'inspectableMap' && <InspectableMap link={selectedInspectableMap} />}
      </main>

      <footer className="footer">
        Un site <a href="https://geodrone3d.com" target="_blank" rel="noopener noreferrer">Geo Drone 3D</a> - © 2023-{new Date().getFullYear()}
      </footer>
    </div>
  );
};

export default ProjectPage